/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import React, { useMemo, useState } from 'react';
import { Checkbox, Drawer, Empty, Input, Popover, Tooltip } from 'antd';
import Table, { TableProps } from 'antd/lib/table';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { ThemedStyleFn, useStyles } from '~src/hooks/useStyles';
// NOTE: UI_SHARE_KEY_STATUS is a real runtime enum (not just a type), and
// `~src/types/db` (no extension) only resolves for type-only imports here -
// webpack can elide those entirely, so it never actually tries to resolve
// the module. The moment a real value is imported, it needs the explicit
// `.d.ts` extension to resolve, matching the same working pattern already
// used for this exact enum in TenderActions.tsx and ImportTab.tsx.
import { DbShare, UI_SHARE_KEY_STATUS } from '~src/types/db.d.ts';
import { formatDate } from '~src/utils/formatDate';
import { filterByQuery } from '~src/utils/searchFilters';
import {
  getShareStatusVisualIntl,
  getUiShareKeyStatus,
} from '~src/utils/shareUtils/useShareKeysStatusUtils';
import IconButton from '~components/_common/IconButton/IconButton';

// Primary-/Secondary Share Keys: status-filter options for this Drawer's
// own table, matching TenderActions.tsx's share filter Popover exactly
// (same UI_SHARE_KEY_STATUS values, same icons/colors/labels/i18n keys) -
// but as LOCAL state here, not the global useStore().filterStates, so
// filtering the Drawer never affects the admin's primary Shares table and
// vice versa.
const ALL_STATUSES: readonly UI_SHARE_KEY_STATUS[] = [
  UI_SHARE_KEY_STATUS.AWAITING,
  UI_SHARE_KEY_STATUS.INVITED,
  UI_SHARE_KEY_STATUS.OPENED,
  UI_SHARE_KEY_STATUS.CLAIMED,
  UI_SHARE_KEY_STATUS.REJECTED,
  UI_SHARE_KEY_STATUS.DELETED,
];

const STATUS_FILTER_OPTIONS: {
  id: UI_SHARE_KEY_STATUS;
  labelId: string;
  color: string;
  icon: string;
}[] = [
  {
    id: UI_SHARE_KEY_STATUS.AWAITING,
    labelId: 'tender-share-key-state-awaiting',
    color: 'grey',
    icon: 'stopwatch',
  },
  {
    id: UI_SHARE_KEY_STATUS.INVITED,
    labelId: 'tender-share-key-state-invited',
    color: 'orange',
    icon: 'envelope',
  },
  {
    id: UI_SHARE_KEY_STATUS.OPENED,
    labelId: 'tender-share-key-state-opened',
    color: 'orange',
    icon: 'envelope-open-text',
  },
  {
    id: UI_SHARE_KEY_STATUS.CLAIMED,
    labelId: 'tender-share-key-state-claimed',
    color: 'green',
    icon: 'check-circle',
  },
  {
    id: UI_SHARE_KEY_STATUS.REJECTED,
    labelId: 'tender-share-key-state-rejected',
    color: 'red',
    icon: 'ban',
  },
  {
    id: UI_SHARE_KEY_STATUS.DELETED,
    labelId: 'tender-share-key-state-deleted',
    color: 'purple',
    icon: 'trash',
  },
];

interface SecondaryShareKeysDrawerProps {
  // Primary vendor row whose "+" was clicked; null when the drawer is closed.
  parentShare: DbShare | null;
  onClose: () => void;
  // Secondary share keys under this primary vendor - fetched live by the
  // caller (TenderSharesListTable.tsx) via useAdminSecondaryShareKeys.ts,
  // using normal admin auth (no `Share-Key` header) against the admin-scoped
  // GET .../share_keys/{share_key}/secondary_share_keys endpoint. This
  // component itself stays a plain, read-only prop with no fetch of its own.
  secondaryShares: DbShare[];
  // Whether the above is still being fetched - shows the Table's built-in
  // loading spinner while switching between different primary rows.
  isLoading?: boolean;
}

export const SecondaryShareKeysDrawer: React.FC<SecondaryShareKeysDrawerProps> = ({
  parentShare,
  onClose,
  secondaryShares,
  isLoading,
}) => {
  const intl = useIntl();
  const styles = useStyles(styleFn);

  const [searchQuery, setSearchQuery] = useState('');
  // Local equivalent of useStore's filterStates/addToFilter/removeFromFilter/
  // setAllSelected/removeAllSelected - deliberately not the global store, so
  // this filter only ever affects this Drawer's own table (see comment on
  // ALL_STATUSES above). Defaults to all statuses selected, matching the
  // global store's own default - this does change today's default (removed/
  // deleted subcontractors were previously hidden unless opted in via the
  // old "Show removed" checkbox); now everything shows unless unchecked.
  const [statusFilter, setStatusFilter] = useState<UI_SHARE_KEY_STATUS[]>([
    ...ALL_STATUSES,
  ]);

  const handleStatusCheckboxChange = (
    id: UI_SHARE_KEY_STATUS,
    checked: boolean
  ) => {
    setStatusFilter((prev) =>
      checked ? [...prev, id] : prev.filter((status) => status !== id)
    );
  };

  // Counts must reflect the full, unfiltered secondaryShares prop (matching
  // TenderActions.tsx's own statusCounts, computed from raw query data) -
  // not filteredList, so the numbers stay stable while filtering.
  const statusCounts = useMemo(() => {
    return secondaryShares.reduce((acc, share) => {
      const status = getUiShareKeyStatus(share);
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    }, {} as Record<number, number>);
  }, [secondaryShares]);

  const filteredList = useMemo(() => {
    return secondaryShares.filter((item) => {
      if (!statusFilter.includes(getUiShareKeyStatus(item))) return false;
      if (
        searchQuery &&
        !filterByQuery(item.share_key_title, searchQuery) &&
        !filterByQuery(item.share_key_email, searchQuery)
      ) {
        return false;
      }
      return true;
    });
  }, [secondaryShares, statusFilter, searchQuery]);

  const filterContent = (
    <div css={styles.filterWrapper}>
      <div css={styles.filterItemWrapper}>
        <div css={styles.filterIconWrapper}>
          {intl.formatMessage({ id: 'show-all' })}
        </div>
        <Checkbox
          checked={statusFilter.length === ALL_STATUSES.length}
          onChange={(e) =>
            setStatusFilter(e.target.checked ? [...ALL_STATUSES] : [])
          }
        />
      </div>

      {STATUS_FILTER_OPTIONS.map(({ id, labelId, color, icon }) => (
        <div css={styles.filterItemWrapper} key={id}>
          <div css={styles.filterIconWrapper}>
            <FontAwesomeIcon icon={['far', icon as any]} style={{ color }} />
            {`${intl.formatMessage({ id: labelId })} (${
              statusCounts[id] ?? 0
            })`}
          </div>
          <Checkbox
            checked={statusFilter.includes(id)}
            onChange={(e) => handleStatusCheckboxChange(id, e.target.checked)}
          />
        </div>
      ))}
    </div>
  );

  const columns: TableProps<DbShare>['columns'] = [
    {
      title: intl.formatMessage({ id: 'status' }),
      dataIndex: 'share_key_state',
      width: 56,
      align: 'center',
      render: (_: any, record: DbShare) => {
        const { icon, color, text } = getShareStatusVisualIntl(intl, record);
        return (
          <Tooltip title={text}>
            <div css={styles.alignCenterWrapper} style={{ color }}>
              {icon}
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: intl.formatMessage({ id: 'company-name' }),
      dataIndex: 'share_key_title',
      ellipsis: true,
    },
    {
      title: intl.formatMessage({ id: 'email' }),
      dataIndex: 'share_key_email',
      ellipsis: true,
    },
    {
      title: intl.formatMessage({ id: 'contact-name' }),
      dataIndex: 'claimer',
      ellipsis: true,
      render: (claimer: DbShare['claimer']) => claimer?.full_name || '',
    },
    {
      title: intl.formatMessage({ id: 'shares-tab-table-opened-column' }),
      dataIndex: 'share_key_opened_at',
      width: 100,
      ellipsis: true,
      render: (openedAt: number | null) => {
        if (!openedAt) return '—';
        const shortDate = formatDate(openedAt, 'short');
        const fullDate = formatDate(openedAt, 'long');
        return (
          <Tooltip title={fullDate} placement="topLeft">
            <span>{shortDate}</span>
          </Tooltip>
        );
      },
    },
  ];

  return (
    <Drawer
      title={parentShare?.share_key_title || parentShare?.share_key_email}
      visible={!!parentShare}
      onClose={onClose}
      width={760}>
      <div css={styles.headerRow}>
        <Input.Search
          allowClear
          css={styles.searchInput}
          placeholder={intl.formatMessage({
            id: 'secondary-share-key-search-placeholder',
          })}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        <Popover
          placement="bottomRight"
          content={filterContent}
          trigger="click">
          <div css={styles.buttonDiv}>
            <IconButton
              iconStyle="far"
              icon="filter-list"
              customStyle={styles.iconsHeader}
              backgroundOff
            />
            <p css={styles.buttonDivLabel}>
              {intl.formatMessage({ id: 'filter' })}
            </p>
          </div>
        </Popover>
      </div>

      <Table<DbShare>
        rowKey={(record) => record.share_key_id}
        columns={columns}
        dataSource={filteredList}
        loading={isLoading}
        pagination={false}
        locale={{
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={intl.formatMessage({ id: 'no-data' })}
            />
          ),
        }}
      />
    </Drawer>
  );
};

const styleFn: ThemedStyleFn = (theme) => ({
  alignCenterWrapper: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  headerRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    marginBottom: 16,
  },
  searchInput: {
    flex: 1,
  },
  buttonDiv: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    cursor: 'pointer',
  },
  buttonDivLabel: {
    marginBottom: 0,
    marginLeft: 2,
  },
  iconsHeader: {
    fontSize: '20px',
    color: theme.colors.primary,
    transition: '0.3s',
  },
  filterWrapper: {
    gap: '5px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    margin: '0 15px',
  },
  filterIconWrapper: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '10px',
  },
  filterItemWrapper: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 20,
  },
});

export default SecondaryShareKeysDrawer;
